import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Project } from "@/integrations/supabase/types/";
import { ProjectCard } from "./ProjectCard";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import { ProjectDetailsDialog } from "./ProjectDetailsDialog";
import { Loader2, FolderOpen } from "lucide-react";
import { useRealtimeSubscription } from "@/hooks/use-realtime";

interface ProjectListProps {
  searchQuery: string;
  statusFilter: string;
  viewMode: "grid" | "list";
  showArchived?: boolean;
  showOnlyMyTasks?: boolean;
}

export const ProjectList = ({ 
  searchQuery, 
  statusFilter, 
  viewMode, 
  showArchived = false,
  showOnlyMyTasks = false 
}: ProjectListProps) => {
  const { toast } = useToast();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  // Enable realtime updates
  useRealtimeSubscription("projects", "projects");
  
  const { data: projects, isLoading, refetch } = useQuery({
    queryKey: ["projects", showArchived, showOnlyMyTasks],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      let query = supabase
        .from("projects")
        .select("*");

      // Filter by archived status
      if (showArchived) {
        query = query.eq("archived", true);
      } else {
        query = query.or("archived.is.null,archived.eq.false");
      }

      // Filter by assigned tasks
      if (showOnlyMyTasks) {
        const { data: tasks } = await supabase
          .from("project_tasks")
          .select("project_id")
          .eq("assigned_to", user.id);
        
        if (tasks && tasks.length > 0) {
          const projectIds = tasks.map(t => t.project_id);
          query = query.in("id", projectIds);
        } else {
          return [];
        }
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load projects",
          variant: "destructive",
        });
        throw error;
      }
      return data as Project[];
    },
  });

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setDetailsOpen(true);
  };

  // Filter projects based on search and status
  const filteredProjects = projects?.filter((project) => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || project.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
        <p className="text-muted-foreground mt-4">Loading projects...</p>
      </div>
    );
  }

  if (!filteredProjects?.length) {
    return (
      <div className="text-center py-12 glass-card rounded-lg">
        <FolderOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">
          {showArchived ? "No Archived Projects" : showOnlyMyTasks ? "No Tasks Assigned" : "No Projects Found"}
        </h3>
        <p className="text-muted-foreground">
          {searchQuery || statusFilter !== "all"
            ? "Try adjusting your filters"
            : showArchived 
              ? "Archive projects to see them here"
              : showOnlyMyTasks
                ? "You don't have any tasks assigned yet"
                : "Create your first project to get started"}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={viewMode === "grid" 
        ? "grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        : "space-y-4"
      }>
        {filteredProjects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            onClick={handleProjectClick}
            viewMode={viewMode}
          />
        ))}
      </div>

      {selectedProject && (
        <ProjectDetailsDialog
          project={selectedProject}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          onUpdate={refetch}
        />
      )}
    </>
  );
};